// The template gallery: twenty editable starting points, grouped occasion
// first, style second, with a "Logo" filter for the ten designs built around
// a logo slot. Each template is a canvas v2 document built in code (one file
// per template) — applying one replaces the design, and every element stays
// editable exactly like a hand-built label.
//
// Adding a template: write `templates/<key>.ts` exporting a LabelTemplate
// (build the wine label with the kit; derive MCC with rebottle() unless the
// design hugs the label edge, then lay it out per bottle; set `slot` when it
// has an image slot), add it to TEMPLATES below, and run the template verify
// script (isCanvasData, ≤ 24 elements, ≤ 12 KB, 3 mm safe margin, fonts and
// colours from the curated sets, no filled shape over the slot). Copy is
// placeholder British English and never a real client name.
//
// An uploaded image always moves to the back of the stack (elements.ts
// placeImage), so nothing filled may sit over a slot: a filled rectangle
// there would hide the logo the moment it lands. Frames around a slot are
// unfilled.

import { loadFonts } from "../fonts";
import { loadOrnaments } from "../ornaments";
import { createCanvas, drawLabel } from "../render";
import type { BottleType } from "../schema";
import { artDeco } from "./art-deco";
import { artLabel } from "./art-label";
import { blackGoldMonogram } from "./black-gold-monogram";
import { boldType } from "./bold-type";
import { chateauClassic } from "./chateau-classic";
import { corporateMark } from "./corporate-mark";
import { heraldicCrest } from "./heraldic-crest";
import { logoBand } from "./logo-band";
import { logoCornerEditorial } from "./logo-corner-editorial";
import { logoFramedDark } from "./logo-framed-dark";
import { logoMedallion } from "./logo-medallion";
import { logoOnly } from "./logo-only";
import { logoPlateDark } from "./logo-plate-dark";
import { logoSeal } from "./logo-seal";
import { logoSplit } from "./logo-split";
import { minimalModern } from "./minimal-modern";
import { photoBand } from "./photo-band";
import { rusticNatural } from "./rustic-natural";
import { sparklingFormal } from "./sparkling-formal";
import type { LabelTemplate, TemplateOccasion, TemplateSlot } from "./types";
import { weddingScript } from "./wedding-script";

export type { LabelTemplate, TemplateOccasion, TemplateSlot } from "./types";

/**
 * Every template, in gallery order (the house-style monogram first). The
 * logo designs are interleaved so "All" shows the whole range; the "Logo"
 * filter gathers them.
 */
export const TEMPLATES: readonly LabelTemplate[] = [
  blackGoldMonogram,
  chateauClassic,
  heraldicCrest,
  logoMedallion,
  logoFramedDark,
  weddingScript,
  corporateMark,
  logoBand,
  logoOnly,
  sparklingFormal,
  logoPlateDark,
  artDeco,
  minimalModern,
  logoCornerEditorial,
  logoSplit,
  boldType,
  artLabel,
  logoSeal,
  rusticNatural,
  photoBand,
];

/** Occasion filters in chip order (the gallery adds its own "All" first). */
export const TEMPLATE_OCCASIONS: readonly { key: TemplateOccasion; label: string }[] = [
  { key: "wedding", label: "Wedding" },
  { key: "corporate", label: "Corporate" },
  { key: "celebration", label: "Celebration" },
  { key: "classic", label: "Classic" },
  { key: "modern", label: "Modern" },
  { key: "sparkling", label: "Sparkling" },
];

export function findTemplate(key: string): LabelTemplate | null {
  return TEMPLATES.find((t) => t.key === key) ?? null;
}

/** The gallery badge for a template's image slot. */
export const TEMPLATE_SLOT_LABELS: Readonly<Record<TemplateSlot, string>> = {
  logo: "Logo slot",
  photo: "Photo slot",
  artwork: "Artwork slot",
};

/** A gallery filter: everything, the logo-slot designs, or one occasion. */
export type TemplateFilter = TemplateOccasion | "all" | "logo";

export function templatesFor(filter: TemplateFilter): readonly LabelTemplate[] {
  if (filter === "all") return TEMPLATES;
  if (filter === "logo") return TEMPLATES.filter((t) => t.slot === "logo");
  return TEMPLATES.filter((t) => t.occasion === filter);
}

/**
 * A gallery tile: the template drawn for `bottle` on a new canvas
 * `widthPx × pixelRatio` device px wide (height from the label's aspect; set
 * the canvas's CSS width to `widthPx`). Awaits the template's fonts and
 * ornaments first, and shows an empty image slot as the editor's dashed
 * placeholder with its caption. Browser only (call from an effect).
 */
export async function renderTemplateThumbnail(
  template: LabelTemplate,
  bottle: BottleType,
  widthPx: number,
  pixelRatio = 1,
): Promise<HTMLCanvasElement> {
  const doc = template.build(bottle);
  const pxPerMm = (widthPx * pixelRatio) / doc.label.width_mm;
  await Promise.all([loadFonts(doc), loadOrnaments(doc, pxPerMm)]);
  const canvas = createCanvas(doc.label.width_mm * pxPerMm, doc.label.height_mm * pxPerMm);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D unavailable");
  drawLabel(ctx, doc, pxPerMm, { uploadedImage: null }, { placeholders: true });
  return canvas;
}
